/**
 * VatSubmissionHistory.js
 *
 * Replaces VatReturnPeriods (System A, removed) in VatDashboard's VAT tab.
 *
 * Shows REAL data only:
 *   - Open/fulfilled obligations from HMRC's own cache: GET /hmrc/obligations
 *   - Real submission history: GET /hmrc/submissions
 *   - Persistent unconfirmed-liability banner: GET /hmrc/unconfirmed-liabilities
 *
 * Open obligations link directly to the real submit screen at
 * /vat/submit/:periodKey (VATSubmission.jsx), which already exists and
 * already expects a periodKey route param.
 *
 * Mounted at /vat/history (linked from VATSubmission.jsx's success screen,
 * which already references this path) and embedded directly in
 * VatDashboard.js's VAT tab.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Button,
    Chip,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
    CircularProgress,
    Alert,
    Tooltip,
    IconButton,
    Collapse
} from '@mui/material';
import {
    Send as SubmitIcon,
    Receipt as ReceiptIcon,
    Refresh as RefreshIcon,
    KeyboardArrowDown,
    KeyboardArrowUp
} from '@mui/icons-material';
import api from './axiosConfig';
import UnconfirmedLiabilitiesPanel from './UnconfirmedLiabilitiesPanel';
import { parseHmrcErrorDetail, getDisplayMessage } from './hmrcErrorParsing';

const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '—';
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2
    }).format(amount);
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB');
};

// status_message is stored server-side as json.dumps(error_data) on rejection
// (see hmrc_routes.py submit_vat_return). error_data is HMRC's own error
// response shape, which varies: sometimes {code, message}, sometimes
// {errors: [{code, message}, ...]}. Parsing of that shape itself now lives
// in hmrcErrorParsing.js, shared with VATSubmission.jsx's live rejection
// display — this is a thin wrapper that JSON.parses the stored string and
// falls back to the raw string if it isn't valid/expected JSON.
const parseRejectionReason = (statusMessage) => {
    if (!statusMessage) return null;
    try {
        const parsed = JSON.parse(statusMessage);
        const detail = parseHmrcErrorDetail(parsed);
        if (detail) {
            return getDisplayMessage(detail);
        }
        // Valid JSON but not a recognised shape — show it raw rather than
        // silently dropping it.
        return JSON.stringify(parsed);
    } catch {
        return statusMessage;
    }
};

const STATUS_COLORS = {
    accepted: 'success',
    rejected: 'error',
    pending: 'warning',
    fulfilled: 'success',
    open: 'default'
};

const VatSubmissionHistory = () => {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [obligations, setObligations] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [unconfirmed, setUnconfirmed] = useState([]);
    const [hmrcNotConnected, setHmrcNotConnected] = useState(false);
    const [expandedSubmissionId, setExpandedSubmissionId] = useState(null);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        setHmrcNotConnected(false);
        try {
            const [obligationsRes, submissionsRes, unconfirmedRes] = await Promise.all([
                api.get('/hmrc/obligations').catch(err => {
                    if (err.response?.status === 400 && err.response?.data?.error === 'HMRC not connected') {
                        setHmrcNotConnected(true);
                        return { data: { obligations: [] } };
                    }
                    throw err;
                }),
                api.get('/hmrc/submissions'),
                api.get('/hmrc/unconfirmed-liabilities')
            ]);

            setObligations(obligationsRes.data?.obligations || []);
            setSubmissions(submissionsRes.data?.submissions || []);
            setUnconfirmed(unconfirmedRes.data?.unconfirmed_submissions || []);
        } catch (err) {
            console.error('Failed to load VAT submission history', err);
            setError(err.response?.data?.error || 'Failed to load VAT obligations and submission history');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    // MTDObligation.status is the raw HMRC value: 'O' (open) or 'F' (fulfilled).
    // to_dict() also provides a status_label ('Open'/'Fulfilled') used for display.
    const openObligations = obligations.filter(o => o.status === 'O');
    const fulfilledObligations = obligations.filter(o => o.status !== 'O');

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {unconfirmed.length > 0 && (
                <UnconfirmedLiabilitiesPanel
                    submissions={unconfirmed}
                    variant="banner"
                    onAllConfirmed={fetchAll}
                />
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Open obligations — what's due now */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid #e5e7eb' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Open VAT Obligations
                    </Typography>
                    <Tooltip title="Refresh from HMRC">
                        <Button size="small" startIcon={<RefreshIcon />} onClick={fetchAll}>
                            Refresh
                        </Button>
                    </Tooltip>
                </Box>

                 {hmrcNotConnected ? (
                    <Alert severity="info">
                        Connect to HMRC to see your open VAT obligations here.
                    </Alert>
                ) : openObligations.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No open obligations from HMRC at this time.
                    </Typography>
                ) : (
                    <Table aria-label="Open VAT obligations">
                        <TableHead>
                            <TableRow>
                                <TableCell component="th" scope="col">Period</TableCell>
                                <TableCell component="th" scope="col">Due Date</TableCell>
                                <TableCell component="th" scope="col">Status</TableCell>
                                <TableCell component="th" scope="col" align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* CHANGED: key was o.period_key, now o.id — period_key is
                                no longer something this component should reference at
                                all, even as a React key (it's still present in the
                                obligations payload for now since MTDObligation.to_dict()
                                returns it, but nothing here should read it). */}
                            {openObligations.map((o) => (
                                <TableRow key={o.id}>
                                    <TableCell>{formatDate(o.period_start)} – {formatDate(o.period_end)}</TableCell>
                                    <TableCell>{formatDate(o.due_date)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={o.status_label || 'Open'}
                                            size="small"
                                            color={STATUS_COLORS.open}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<SubmitIcon />}
                                            // CHANGED: navigate by o.id (internal obligation
                                            // row id), not o.period_key. period_key never
                                            // appears in the URL now.
                                            onClick={() => navigate(`/vat/submit/${o.id}`)}
                                            disabled={unconfirmed.length > 0}
                                        >
                                            Submit Return
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            {/* Submission history — what's already gone to HMRC */}
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #e5e7eb' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Submission History
                </Typography>

                {submissions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No VAT returns have been submitted through SalonLogic Pro yet.
                    </Typography>
                ) : (
                    <Table aria-label="VAT submission history">
                        <TableHead>
                            <TableRow>
                                <TableCell component="th" scope="col" sx={{ width: 40 }} />
                                <TableCell component="th" scope="col">Period</TableCell>
                                <TableCell component="th" scope="col">Submitted</TableCell>
                                <TableCell component="th" scope="col">Status</TableCell>
                                <TableCell component="th" scope="col">HMRC Receipt</TableCell>
                                <TableCell component="th" scope="col">Charge Ref</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {submissions.map((s) => {
                                const isExpanded = expandedSubmissionId === s.id;
                                const rejectionReason = s.status === 'rejected' ? parseRejectionReason(s.status_message) : null;
                                return (
                                    <React.Fragment key={s.id}>
                                        <TableRow>
                                            <TableCell>
                                                {rejectionReason && (
                                                    <IconButton
                                                        size="small"
                                                        aria-label={isExpanded ? 'Hide rejection reason' : 'Show rejection reason'}
                                                        onClick={() => setExpandedSubmissionId(isExpanded ? null : s.id)}
                                                    >
                                                        {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                            <TableCell>{formatDate(s.period_start)} – {formatDate(s.period_end)}</TableCell>
                                            <TableCell>{formatDate(s.submitted_at)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={s.status}
                                                    size="small"
                                                    color={STATUS_COLORS[s.status] || 'default'}
                                                    sx={{ textTransform: 'capitalize' }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                {s.hmrc_receipt_id || '—'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                {s.charge_ref_number || '—'}
                                            </TableCell>
                                        </TableRow>
                                        {rejectionReason && (
                                            <TableRow>
                                                <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                        <Alert severity="error" sx={{ m: 1, borderRadius: 1 }}>
                                                            <Typography variant="body2">
                                                                <strong>HMRC rejection reason:</strong> {rejectionReason}
                                                            </Typography>
                                                        </Alert>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Paper>
        </Box>
    );
};

export default VatSubmissionHistory;